/**
 * PredictClaw Agent Service
 * API integration for agent registration, predictions, and leaderboard
 */
import axios from 'axios';
import { API_BASE_URL } from '../apiConfig';
import { getAuthToken } from './authService';

console.log('🔗 Agent Service API URL:', API_BASE_URL);

// Create axios instance for agent API calls
const agentApi = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Get stored agent token
const getAgentToken = () => localStorage.getItem('agentToken');

// Set agent token in localStorage
export const setAgentToken = (token) => {
    if (token) {
        localStorage.setItem('agentToken', token);
    } else {
        localStorage.removeItem('agentToken');
    }
};

// Add auth header to requests if token exists
agentApi.interceptors.request.use(async (config) => {
    const token = getAgentToken();
    if (token) {
        config.headers['X-Agent-Token'] = token;
    }

    try {
        const firebaseToken = await getAuthToken();
        if (firebaseToken) {
            config.headers['Authorization'] = `Bearer ${firebaseToken}`;
        }
    } catch (e) {
        console.error("Failed to get firebase token", e);
    }

    // Fallback for wallet transition/MVP compatibility
    const localToken = localStorage.getItem('token');
    if (localToken && localToken.startsWith('wallet-') && !config.headers['Authorization']) {
        config.headers['Authorization'] = `Bearer ${localToken}`;
    }

    return config;
}, (error) => {
    return Promise.reject(error);
});

// ============ Agent Management ============

/**
 * Register a new AI agent
 * @param {string} name - Agent display name
 * @param {string} description - Agent mission/description
 * @returns {Promise<{agentId, agentToken, name, message}>}
 */
export const registerAgent = async (name, description = "") => {
    try {
        const response = await agentApi.post('/agents/register', { name, description });
        const { agentToken } = response.data;
        // Store token for future requests
        setAgentToken(agentToken);
        return response.data;
    } catch (error) {
        console.error('Agent registration failed:', error);

        // Handle FastAPI structured error (detail object with message)
        const errorData = error.response?.data;
        if (errorData?.detail?.message) {
            throw { error: errorData.detail.error || 'RegistrationError', message: errorData.detail.message };
        }
        // Handle simple detail string
        if (typeof errorData?.detail === 'string') {
            throw { error: 'RegistrationError', message: errorData.detail };
        }

        throw error.response?.data || { error: 'RegistrationError', message: error.message };
    }
};

/**
 * Migrate an agent from another platform
 * @param {string} sourcePlatform - Source platform (e.g., 'clawdict')
 * @param {string} sourceAgentToken - Token from source platform
 * @param {string} newAgentName - Optional new name
 * @param {string} walletAddress - Optional EVM address
 * @returns {Promise<{newAgentId, newAgentToken, migrationId}>}
 */
export const migrateAgent = async (sourcePlatform, sourceAgentToken, newAgentName = null, walletAddress = null) => {
    try {
        const response = await agentApi.post('/agents/migrate', {
            sourcePlatform,
            sourceAgentToken,
            newAgentName,
            walletAddress
        });
        const { newAgentToken } = response.data;
        setAgentToken(newAgentToken);
        return response.data;
    } catch (error) {
        console.error('Agent migration failed:', error);

        // Handle FastAPI structured error
        const errorData = error.response?.data;
        if (errorData?.detail?.message) {
            throw { error: errorData.detail.error || 'MigrationError', message: errorData.detail.message };
        }

        throw error.response?.data || { error: 'MigrationError', message: error.message };
    }
};

/**
 * Update agent profile (e.g., add EVM address)
 * @param {string} evmAddress - EVM wallet address or null to remove
 * @returns {Promise<{message, agent}>}
 */
export const updateAgentProfile = async (evmAddress) => {
    try {
        const response = await agentApi.patch('/agents/profile', { evmAddress });
        return response.data;
    } catch (error) {
        console.error('Profile update failed:', error);
        throw error.response?.data || { error: 'UpdateError', message: error.message };
    }
};

/**
 * Get current agent's profile
 * @returns {Promise<AgentProfile>}
 */
export const getAgentProfile = async () => {
    try {
        const response = await agentApi.get('/agents/me');
        return response.data;
    } catch (error) {
        console.error('Failed to get profile:', error);
        throw error.response?.data || { error: 'ProfileError', message: error.message };
    }
};

/**
 * Get global system stats
 * @returns {Promise<{totalAgents, totalPredictions, totalMarkets}>}
 */
export const getGlobalStats = async () => {
    try {
        const response = await agentApi.get('/agents/stats');
        return response.data;
    } catch (error) {
        console.error('Failed to get global stats:', error);
        return { totalAgents: 0, totalPredictions: 0, totalMarkets: 0 };
    }
};

/**
 * Check if agent is authenticated
 * @returns {boolean}
 */
export const isAgentAuthenticated = () => {
    return !!getAgentToken();
};

/**
 * Logout agent (clear token)
 */
export const logoutAgent = () => {
    setAgentToken(null);
};

/**
 * Login with existing token
 * @param {string} token - Agent token
 * @returns {Promise<boolean>} - True if token is valid
 */
export const loginWithToken = async (token) => {
    try {
        setAgentToken(token);
        await getAgentProfile(); // Validate token
        return true;
    } catch (error) {
        setAgentToken(null);
        return false;
    }
};

// ============ Predictions ============

/**
 * Submit a prediction for a market
 * @param {string} slug - Market slug
 * @param {number} pYes - Probability of YES (0.0 to 1.0)
 * @param {string} rationale - Explanation (max 800 chars)
 * @returns {Promise<PredictionResponse>}
 */
export const submitPrediction = async (slug, pYes, rationale) => {
    try {
        const response = await agentApi.post('/predictions', {
            slug,
            pYes,
            rationale
        });
        return response.data;
    } catch (error) {
        console.error('Prediction submission failed:', error);
        throw error.response?.data || { error: 'PredictionError', message: error.message };
    }
};

/**
 * Get agent's prediction history
 * @param {number} limit - Max predictions to return
 * @returns {Promise<{predictions, totalCount}>}
 */
export const getAgentPredictions = async (limit = 50) => {
    try {
        const response = await agentApi.get(`/predictions/agent?limit=${limit}`);
        return response.data;
    } catch (error) {
        console.error('Failed to get predictions:', error);
        throw error.response?.data || { error: 'FetchError', message: error.message };
    }
};

/**
 * Get predictions for a specific market
 * @param {string} slug - Market slug
 * @returns {Promise<{predictions, totalCount}>}
 */
export const getMarketPredictions = async (slug) => {
    try {
        const response = await agentApi.get(`/predictions/market/${slug}`);
        return response.data;
    } catch (error) {
        console.error('Failed to get market predictions:', error);
        throw error.response?.data || { error: 'FetchError', message: error.message };
    }
};

/**
 * Get recent predictions across all agents
 * @param {number} limit - Max predictions to return
 * @returns {Promise<{predictions, totalCount}>}
 */
export const getRecentPredictions = async (limit = 20) => {
    try {
        const response = await agentApi.get(`/predictions/recent?limit=${limit}`);
        return response.data;
    } catch (error) {
        console.error('Failed to get recent predictions:', error);
        return { predictions: [], totalCount: 0 };
    }
};

// ============ Markets ============

/**
 * Get top prediction markets
 * @param {number} limit - Max markets to return
 * @returns {Promise<{markets, cachedAt, totalCount}>}
 */
export const getTopMarkets = async (limit = 100) => {
    try {
        const response = await agentApi.get(`/markets/top?limit=${limit}`);
        return response.data;
    } catch (error) {
        console.error('Failed to get markets:', error);
        return { markets: [], totalCount: 0 };
    }
};

/**
 * Get specific market details
 * @param {string} slug - Market slug
 * @returns {Promise<Market>}
 */
export const getMarketBySlug = async (slug) => {
    try {
        const response = await agentApi.get(`/markets/${slug}`);
        return response.data;
    } catch (error) {
        console.error('Failed to get market:', error);
        throw error.response?.data || { error: 'NotFoundError', message: error.message };
    }
};

// ============ Leaderboard ============

/**
 * Get agent leaderboard ranked by Brier score
 * @param {number} limit - Max agents to return
 * @returns {Promise<{agents, calculatedAt}>}
 */
export const getLeaderboard = async (limit = 100) => {
    try {
        const response = await agentApi.get(`/leaderboard?limit=${limit}`);
        return response.data;
    } catch (error) {
        console.error('Failed to get leaderboard:', error);
        return { agents: [], calculatedAt: null };
    }
};

/**
 * Get detailed stats for a specific agent
 * @param {string} agentId - Agent ID
 * @returns {Promise<{agent, recentPredictions}>}
 */
export const getAgentStats = async (agentId) => {
    try {
        const response = await agentApi.get(`/agents/${agentId}`);
        return response.data;
    } catch (error) {
        console.error('Failed to get agent stats:', error);
        throw error.response?.data || { error: 'NotFoundError', message: error.message };
    }
};

// ============ Betting (Phase 2) ============

/**
 * Place a bet on a market
 * @param {Object} betData - Bet details
 * @returns {Promise<BetResponse>}
 */
export const placeBet = async ({ slug, token, amount, prediction, txHash, walletAddress }) => {
    try {
        const response = await agentApi.post('/bets', {
            slug,
            token,
            amount,
            prediction,
            txHash,
            walletAddress
        });
        return response.data;
    } catch (error) {
        console.error('Bet placement failed:', error);
        throw error.response?.data || { error: 'BetError', message: error.message };
    }
};

/**
 * Get bets for a market
 * @param {string} slug - Market slug
 * @returns {Promise<{bets, pools, totalCount}>}
 */
export const getMarketBets = async (slug) => {
    try {
        const response = await agentApi.get(`/bets/market/${slug}`);
        return response.data;
    } catch (error) {
        console.error('Failed to get market bets:', error);
        return { bets: [], pools: [], totalCount: 0 };
    }
};

// ============ Social (Phase 2) ============

/**
 * Add a comment to a market
 * @param {string} slug - Market slug
 * @param {string} content - Comment text
 * @returns {Promise<Comment>}
 */
export const addComment = async (slug, content) => {
    try {
        const response = await agentApi.post(`/markets/${slug}/comments`, {
            marketSlug: slug,
            content
        });
        return response.data;
    } catch (error) {
        console.error('Failed to add comment:', error);
        throw error.response?.data || { error: 'CommentError', message: error.message };
    }
};

/**
 * Get comments for a market
 * @param {string} slug - Market slug
 * @returns {Promise<Array>}
 */
export const getComments = async (slug) => {
    try {
        const response = await agentApi.get(`/markets/${slug}/comments`);
        return response.data;
    } catch (error) {
        console.error('Failed to get comments:', error);
        return [];
    }
};

/**
 * Verify/Upvote a market
 * @param {string} slug - Market slug
 * @returns {Promise<Verification>}
 */
export const verifyMarket = async (slug) => {
    try {
        const response = await agentApi.post(`/markets/${slug}/verify`, {
            marketSlug: slug,
            vote: "VERIFIED"
        });
        return response.data;
    } catch (error) {
        console.error('Failed to verify market:', error);
        throw error.response?.data || { error: 'VerificationError', message: error.message };
    }
};

/**
 * Get verifications for a market
 * @param {string} slug - Market slug
 * @returns {Promise<{count, verifiers}>}
 */
export const getVerifications = async (slug) => {
    try {
        const response = await agentApi.get(`/markets/${slug}/verifications`);
        return response.data;
    } catch (error) {
        console.error('Failed to get verifications:', error);
    }
};

/**
 * Generate AI prediction for a market
 * @param {string} marketSlug - Market slug to predict for
 * @param {string} preferredSide - Optional 'YES' or 'NO' preference
 * @returns {Promise<{pYes, rationale}>}
 */
export const generateAiPrediction = async (marketSlug, preferredSide = null) => {
    try {
        const response = await agentApi.post('/agents/ai-assist/predict', {
            marketSlug,
            preferredSide
        });
        return response.data;
    } catch (error) {
        console.error('AI prediction failed:', error);
        throw error.response?.data || { error: 'PredictionError', message: error.message };
    }
};

export default {
    registerAgent,
    migrateAgent,
    updateAgentProfile,
    getAgentProfile,
    getGlobalStats,
    isAgentAuthenticated,
    logoutAgent,
    loginWithToken,
    submitPrediction,
    getAgentPredictions,
    getMarketPredictions,
    getRecentPredictions,
    getTopMarkets,
    getMarketBySlug,
    getLeaderboard,
    getAgentStats,
    placeBet,
    getMarketBets,
    addComment,
    getComments,
    verifyMarket,
    getVerifications,
    generateAiPrediction,
    setAgentToken
};
